/**
 * @fileoverview Application entry point for Absolute Jobs React application
 * Initializes React 18 root, imports global styles, and configures FontAwesome icons
 * Sets up performance monitoring and renders the main App component
 * 
 * @version 1.0.0
 * @author Absolute Jobs Team
 * @security Uses React 18 Strict Mode for enhanced development safety
 */

import React from "react";
import ReactDOM from "react-dom/client";

// Core application styles
import "./index.css";

// Global component styles
import "static/css/common.css";     // Common utility classes
import "static/css/variables.css";  // CSS custom properties/variables
import "static/css/input.css";      // Form input styling
import "static/css/header.css";     // Header component styling
import "static/css/button.css";     // Button component styling
import "static/css/table.css";      // Table component styling
import "static/css/multiSelect.css"; // Multi-select component styling
import "static/css/toggle.css";     // Toggle switch styling

// Main application component
import App from "./App";

// Performance monitoring utility
import reportWebVitals from "./reportWebVitals";

// Third-party library styles
import "bootstrap/dist/css/bootstrap.min.css";  // Bootstrap CSS framework
import "bootstrap/dist/js/bootstrap.bundle.min"; // Bootstrap JavaScript components
import "react-phone-number-input/style.css";    // Phone number input styling

// FontAwesome icon configuration
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";

/**
 * Configure FontAwesome icon library
 * Adds all solid icons to the global library for use throughout the application
 */
library.add(fas);

/**
 * Initialize React 18 root and render the application
 * Uses createRoot API for improved performance and concurrent features
 * 
 * @security React.StrictMode enables additional checks and warnings in development
 */
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Render the main application with React 18 Strict Mode
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

/**
 * Initialize performance monitoring
 * Measures and reports Core Web Vitals for performance optimization
 * 
 * @note To enable performance logging, pass a function to reportWebVitals:
 * reportWebVitals(console.log) - Log to console
 * reportWebVitals(sendToAnalytics) - Send to analytics service
 * 
 * @see https://bit.ly/CRA-vitals for more information about Web Vitals
 */
reportWebVitals();
